import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react'
import { type PointerEvent, useLayoutEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'

import { QuantityBadge } from '../quantity-badge'
import { SCROLL_AMOUNT, STATUS_TAB_FALLBACK_COLOR } from './status-tabs.constants'
import * as S from './status-tabs.styled'
import type { StatusTabsProps } from './status-tabs.types'

export const StatusTabs = <T extends string = string>({
  options,
  value,
  onChange,
  disabled,
  ...props
}: StatusTabsProps<T>) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [hasOverflow, setHasOverflow] = useState(false)
  const isPointerDownRef = useRef(false)
  const startXRef = useRef(0)
  const startScrollLeftRef = useRef(0)

  const checkForOverflow = () => {
    const el = containerRef.current
    if (!el) return
    setHasOverflow(el.scrollWidth > el.clientWidth)
  }

  useLayoutEffect(() => {
    checkForOverflow()
  }, [options])

  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return
    const resizeObserver = new ResizeObserver(checkForOverflow)
    resizeObserver.observe(el)
    return () => resizeObserver.disconnect()
  }, [])

  const handleScrollLeft = () =>
    containerRef.current?.scrollBy({ behavior: 'smooth', left: -SCROLL_AMOUNT })
  const handleScrollRight = () =>
    containerRef.current?.scrollBy({ behavior: 'smooth', left: SCROLL_AMOUNT })

  const handlePointerDown = (e: PointerEvent<HTMLDivElement>) => {
    isPointerDownRef.current = true
    startXRef.current = e.clientX
    startScrollLeftRef.current = e.currentTarget.scrollLeft
  }

  const handlePointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!isPointerDownRef.current) return
    const dx = e.clientX - startXRef.current
    if (Math.abs(dx) < 1) return
    e.currentTarget.scrollLeft = startScrollLeftRef.current - dx
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  const handlePointerUpOrCancel = (e: PointerEvent<HTMLDivElement>) => {
    isPointerDownRef.current = false
    e.currentTarget.releasePointerCapture(e.pointerId)
  }

  return (
    <S.ScrollWrapper>
      {hasOverflow && (
        <Button onlyBox use="ghost" size="small" title="Scroll left" onClick={handleScrollLeft}>
          <Icon icon={CaretLeftIcon} />
        </Button>
      )}
      <S.ScrollableArea>
        <S.Container
          {...props}
          ref={containerRef}
          onPointerCancel={handlePointerUpOrCancel}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUpOrCancel}
        >
          {options.map((option) => {
            const isActive = value === option.value

            return (
              <S.Tab
                key={option.value}
                type="button"
                $active={isActive}
                $disabled={!!disabled}
                onClick={() => !disabled && onChange(option.value)}
              >
                {option.label}
                {option.count != null && (
                  <QuantityBadge
                    color={
                      option.count === 0
                        ? STATUS_TAB_FALLBACK_COLOR
                        : (option.color ?? STATUS_TAB_FALLBACK_COLOR)
                    }
                    quantity={option.count}
                  />
                )}
              </S.Tab>
            )
          })}
        </S.Container>
      </S.ScrollableArea>
      {hasOverflow && (
        <Button onlyBox use="ghost" size="small" title="Scroll right" onClick={handleScrollRight}>
          <Icon icon={CaretRightIcon} />
        </Button>
      )}
    </S.ScrollWrapper>
  )
}
